import api from "../../config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const workgroupsPag = createPaginationModule('WORKGROUPS');
import LoggerService from "@/services/LoggerService";

const state = {
    allWorkgroups: [],
    skillsForWorkgroup: [],
    skillPersons: [],
    loading: false,
    error: null,
    pagination: defaultPagination(),
};

const mutations = {
    ...workgroupsPag.mutations,

    SET_WORKGROUPS(state, payload) {
        state.allWorkgroups = payload;
    },
    SET_ALL_SKILLS(state, payload) {
        state.skillsForWorkgroup = payload;
    },
    SET_SKILL_PERSONS(state, payload) {
        state.skillPersons = payload;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

const actions = {

    async fetchWorkgroups({ commit }, payload) {
        try {
            const paginationParams = payload.all ? "all=true" : `page_size=${payload.pageSize}&page_number=${payload.page}&search=${encodeURIComponent(payload.search || '')}`
            const response = await api.get(`/activities/${payload.activityId}/workgroups?${paginationParams}`);
            commit("SET_WORKGROUPS", response.data?.data?.workgroups);

            if (response.data.data.pagination) {
                commit(workgroupsPag.SET_PAGINATION, {
                    currentPage: response.data.data.pagination.current_page,
                    totalPages: response.data.data.pagination.total_pages,
                    totalRecords: response.data.data.pagination.total_records,
                    pageSize: response.data.data.pagination.page_size,
                    hasPrevious: response.data.data.pagination.has_previous,
                    hasNext: response.data.data.pagination.has_next,
                });
            }

            return response.data?.data?.workgroups;

        } catch (error) {
            throw error.response?.data?.message || "Failed to fetch workgroups";
        }
    },
    async fetchFutureActivityWorkgroupInstances(_, workgroupId) {
        const perPage = 100;
        let page = 1;
        let totalPages = 1;
        const instances = [];

        try {
            do {
                const response = await api.get(`/activity-workgroups/${workgroupId}/instances`, {
                    params: {
                        include_past: false,
                        per_page: perPage,
                        page,
                    },
                });
                const data = response.data?.data || {};
                instances.push(...(data.instances || []));
                totalPages = Math.max(1, Number(data.pagination?.total_pages) || 1);
                page += 1;
            } while (page <= totalPages);

            return instances;
        } catch (error) {
            throw error.response?.data?.error || error.response?.data?.message || "Failed to fetch future workgroup instances";
        }
    },
    async fetchAllSkillsByLocation({ commit }, locationId) {
        try {
            const response = await api.get(`/skills/location/${locationId}?all=true`);
            var skillsOption = response.data?.data.output.map(skill => ({
                label: skill.skillname,
                value: skill.skillid,
            }));
            commit("SET_ALL_SKILLS", skillsOption);
            return skillsOption;
        } catch (error) {
            const message = error.response?.data?.message || "Failed to fetch skills by location";
            commit("SET_ALL_SKILLS", []);
            throw message;
        }
    },
    async createWorkgroup({ rootGetters }, payload) {
        try {
            const requestBody = {
                cmd: "addworkgroup",
                ...payload,
            };
            const response = await api.post('/manageactivityajax/', requestBody);
            
            // Log workgroup creation
            const currentUser = rootGetters.stSessionData;
            await LoggerService.logDataOperation(
                'INSERT',
                'workgroup',
                response.data?.workgroupid || 'new',
                {
                    activityId: payload.activityid,
                    workgroupName: payload.workgroupname
                },
                currentUser?.personid
            );
            
            return response.data;
        } catch (error) {
            // Log failed creation
            const currentUser = rootGetters.stSessionData;
            await LoggerService.logDataOperation(
                'INSERT_FAILED',
                'workgroup',
                'new',
                { error: error.message, payload },
                currentUser?.personid
            );
            
            throw error.response?.data?.message || "Failed to create workgroup";
        }
    },

    /**
     * ✅ Get skill persons
     * API: POST /manageskillajax
     * cmd: "getskillpersonsjson"
     */
    async getSkillPersonsForWorkgroup({ commit }, { skillId, locationId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageskillajax", {
                cmd: "getskillpersonsjson",
                skillid: skillId,
                locationid: locationId,
            });

            // Response can be a direct array or wrapped in an object
            const responseData = response.data?.data || response.data?.output || response.data || [];
            const skillPersons = Array.isArray(responseData) 
                ? responseData 
                : (responseData.persons_with_skill || responseData.all_volunteers || []);
            
            commit("SET_SKILL_PERSONS", skillPersons);
            return skillPersons;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch skill persons");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    allWorkgroups: (state) => state.allWorkgroups,
    skillsForWorkgroup: (state) => state.skillsForWorkgroup,
    skillPersonsForWorkgroup: (state) => state.skillPersons,
    workgroupPagination: (state) => state.pagination,
    workgroupLoading: (state) => state.loading,
    workgroupError: (state) => state.error,
};

export default {
    state,
    mutations,
    actions,
    getters,
};
